import { Button, cn } from '@repo/ui'
import { AlertTriangle, FileDiff, FilePen, FileText, HelpCircle, Presentation, RotateCw } from 'lucide-react'
import { useRef, useState } from 'react'
import { changeSizeLabel, slotPhrase } from './eventCopy'
import {
  activityAction,
  activityDetail,
  activityGlyph,
  activityNotable,
  activityQuiet,
  activityRow
} from './activityLine'
import type { ConversationChange } from './state'
import type { ChatInspectorIdentity, WorkspaceCanvasIdentity } from './ConversationShell'

type ChangeDestination =
  { surface: 'canvas'; identity: WorkspaceCanvasIdentity } | { surface: 'inspector'; identity: ChatInspectorIdentity }

/**
 * Where a change card's Open button goes, or nothing when the change cannot address a
 * surface. The button's presence is derived from this rather than from the change kind
 * alone: a card whose ids do not resolve used to render an Open button that did nothing
 * when clicked, which is BUG-402. A brief is this chat's working memory and an artifact
 * is the workspace's, so the destination is the only thing that says which is which.
 *
 * The brief destination survived a proposal to remove it. The card that prompted that
 * read `Brief updated` and named nothing, which is what made its button look pointless;
 * that card no longer exists, because a delta naming no slots now renders nothing at all.
 * What remains says which slots moved, and its button is the only route to the facts
 * panel anywhere in the chat: without it the panel is reachable only by URL.
 */
function changeDestination(change: ConversationChange): ChangeDestination | undefined {
  if (change.kind === 'brief') {
    return { surface: 'inspector', identity: { inspector: 'facts', briefId: change.briefId } }
  }
  if (change.kind !== 'artifact') return undefined
  switch (change.artifactType) {
    // A created proposal opens as a whole: it has no section to focus yet.
    case 'proposal':
      return change.proposalId !== undefined
        ? { surface: 'canvas', identity: { canvas: 'proposal', proposalId: change.proposalId } }
        : undefined
    case 'proposal-section':
      return change.proposalId !== undefined && change.sectionId !== undefined
        ? {
            surface: 'canvas',
            identity: { canvas: 'proposal', proposalId: change.proposalId, sectionId: change.sectionId }
          }
        : undefined
    case 'presentation':
      return change.presentationId !== undefined
        ? { surface: 'canvas', identity: { canvas: 'presentation', presentationId: change.presentationId } }
        : undefined
    // The event still carries a versionId on the wire; the address deliberately drops it,
    // because a version only ever fed a staleness guard that made the row's own button
    // dead-end once the slide was refined again.
    case 'presentation-slide':
      return change.presentationId !== undefined && change.slideId !== undefined
        ? {
            surface: 'canvas',
            identity: { canvas: 'presentation', presentationId: change.presentationId, slideId: change.slideId }
          }
        : undefined
    case 'document':
      return change.documentId !== undefined
        ? { surface: 'canvas', identity: { canvas: 'document', documentId: change.documentId } }
        : undefined
  }
}

// The icon says which kind of thing changed. A single catch-all cannot: the kinds that
// still reach a card are a brief edit, a question, a refusal and a stream error, and
// badging a refusal as a file change (the old FileDiff) or as a brief edit is wrong in
// the same way, in the glyph and in data-change-icon.
function changeIcon(change: ConversationChange): { Icon: typeof FileDiff; iconName: string } {
  if (change.kind === 'status') return { Icon: HelpCircle, iconName: 'help' }
  if (change.kind === 'error') return { Icon: AlertTriangle, iconName: 'alert' }
  if (change.kind === 'question') return { Icon: HelpCircle, iconName: 'question' }
  if (change.kind !== 'artifact') return { Icon: FilePen, iconName: 'brief' }
  switch (change.artifactType) {
    case 'document':
      return { Icon: FileText, iconName: 'document' }
    case 'presentation':
    case 'presentation-slide':
      return { Icon: Presentation, iconName: 'presentation' }
    case 'proposal':
    case 'proposal-section':
      return { Icon: FileDiff, iconName: 'diff' }
  }
}

// How much of the artifact moved. The reading itself lives in eventCopy, because a slide
// turn shows the same figure and the two must not drift.
function changeSize(change: ConversationChange): string | undefined {
  return change.kind === 'artifact' ? changeSizeLabel(change.words, change.previousWords) : undefined
}

// A row says what happened and to what. Returning the target name in place of the label
// split one change across two rows, one naming the deck and never saying what happened to
// it, the other saying what happened and never naming the deck.
//
// Name first, verb second: the row's width is shared with the icon, the size and the Open
// button, and the name is the half that distinguishes one row from the next, so it is the
// verb that gives way when the row runs out of room.
function changeTitle(change: ConversationChange): string {
  if (change.kind !== 'artifact' || change.targetName === undefined) return change.label
  return change.targetName
}

// The verb, where the title above is carrying the name instead.
function changeSubtitle(change: ConversationChange): string | undefined {
  return change.kind === 'artifact' && change.targetName !== undefined ? change.label : undefined
}

const unsupportedDetailPrefix = "I can't help with that. Here is what I can do:"

function statusGuidance(detail: string | undefined): string | undefined {
  const message = detail?.trim()
  if (message === undefined || message === '') return undefined
  if (!message.startsWith(unsupportedDetailPrefix)) return message

  return 'Try asking for help preparing the content or working through the next step.'
}

export function ConversationChangeCard({
  change,
  onOpenCanvas,
  onOpenInspector
}: {
  change: ConversationChange
  onOpenCanvas: (identity: WorkspaceCanvasIdentity) => void
  onOpenInspector: (identity: ChatInspectorIdentity) => void
}) {
  const destination = changeDestination(change)
  const size = changeSize(change)
  const subtitle = changeSubtitle(change)
  const { Icon, iconName } = changeIcon(change)
  if (change.kind === 'status') {
    const guidance = statusGuidance(change.detail)
    return (
      <article
        className="flex min-h-6 items-start gap-2 py-0.5 text-[0.8125rem] leading-5"
        aria-label="Conversation change"
      >
        <span
          className={cn(activityGlyph, activityQuiet, 'mt-0.5')}
          data-testid="conversation-change-icon"
          data-change-icon={iconName}
        >
          <Icon aria-hidden="true" />
        </span>
        <span className="max-w-[68ch] min-w-0 flex-1">
          <span className="block font-medium text-foreground">{changeTitle(change)}</span>
          {guidance !== undefined && <span className="mt-0.5 block text-muted-foreground">{guidance}</span>}
        </span>
      </article>
    )
  }

  // A stream error is the one informational row a reader must not skim past, so it
  // keeps foreground text and a warning tint where the rest go muted.
  const notable = change.kind === 'error'
  return (
    <article className={activityRow} aria-label="Conversation change">
      <span
        className={cn(activityGlyph, notable ? activityNotable : activityQuiet)}
        data-testid="conversation-change-icon"
        data-change-icon={iconName}
      >
        <Icon aria-hidden="true" />
      </span>
      {/*
        Name and verb share one line now, where the card stacked them. The card's reason
        for splitting was that a joined string spends the row's width on the verb and
        ellipses the name; truncating each half independently keeps that, because the verb
        is what gives way first while the name is the half that distinguishes one row from
        the next.
      */}
      <span className="flex min-w-0 flex-1 items-center gap-1.5">
        <span className={cn('min-w-0 truncate', notable ? 'text-foreground' : 'text-muted-foreground')}>
          {changeTitle(change)}
        </span>
        {subtitle !== undefined && (
          <>
            <span className="shrink-0 text-muted-foreground/50" aria-hidden="true">
              &middot;
            </span>
            {/* Shrinks three times as fast as the name, so a narrow row spends its last
                characters on the half that distinguishes one row from the next. */}
            <span className="min-w-0 shrink-[3] truncate text-muted-foreground/70">{subtitle}</span>
          </>
        )}
      </span>
      {size !== undefined && (
        <span className="shrink-0 text-xs text-muted-foreground/60 tabular-nums" data-testid="conversation-change-size">
          {size}
        </span>
      )}
      {destination !== undefined && (
        <Button
          type="button"
          size="sm"
          variant="link"
          className="ml-1 h-auto shrink-0 px-0 text-xs"
          onClick={() =>
            destination.surface === 'canvas'
              ? onOpenCanvas(destination.identity)
              : onOpenInspector(destination.identity)
          }
        >
          Open
        </Button>
      )}
    </article>
  )
}

/**
 * A question run that spent its ask budget with required slots still missing. Exhaustion
 * used to end the task silently, so the chat looked finished while the work could not run.
 */
export function ConversationExhaustedCard({
  entry,
  onContinue
}: {
  entry: { runId: number; briefId: number; remainingSlotKeys: Array<string> }
  onContinue?: (briefId: number, runId: number) => Promise<void>
}) {
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string>()
  const needed = entry.remainingSlotKeys.map(slotPhrase).filter((phrase) => phrase !== '')
  return (
    <article className="flex flex-col gap-1" aria-label="Details still needed">
      <span className={activityRow}>
        <span className={cn(activityGlyph, activityNotable)}>
          <HelpCircle aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1 truncate">I stopped asking, and this task still needs more</span>
        {onContinue !== undefined && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className={activityAction}
            disabled={pending}
            onClick={() => {
              setPending(true)
              setError(undefined)
              void onContinue(entry.briefId, entry.runId)
                .catch(() => setError('That did not go through. Try again.'))
                .finally(() => setPending(false))
            }}
          >
            {pending ? 'Continuing' : 'Answer the rest'}
          </Button>
        )}
      </span>
      {needed.length > 0 && (
        <span className={cn(activityDetail, 'text-muted-foreground')}>Still needed: {needed.join(', ')}.</span>
      )}
      {error !== undefined && (
        <span className={cn(activityDetail, 'text-destructive')} role="alert">
          {error}
        </span>
      )}
    </article>
  )
}

/**
 * An execution that stopped short, where the user's next move is retrying. The same
 * control exists in the facts panel; this one is where the stop is actually reported.
 *
 * A cancellation is reported here too, without a retry: the reader asked for the stop, so
 * there is nothing to recover, but the panel drops its own Cancel and Retry controls the
 * moment the status lands and the transcript cards no lifecycle status. Without this the
 * chat went silent on a run the reader had just stopped.
 */
export function ConversationIntakeStoppedCard({ reason }: { reason: 'failed' | 'cancelled' }) {
  const cancelled = reason === 'cancelled'
  return (
    <article className="flex flex-col gap-1" aria-label={cancelled ? 'Task cancelled' : 'Could not start this task'}>
      <span className={activityRow}>
        <span className={cn(activityGlyph, cancelled ? activityQuiet : activityNotable)}>
          <AlertTriangle aria-hidden="true" />
        </span>
        <span>{cancelled ? 'Task cancelled' : 'Could not start this task'}</span>
      </span>
      {!cancelled && <span className={activityDetail}>Check your sources and send your request again.</span>}
    </article>
  )
}

export function ConversationStoppedCard({
  executionId,
  reason = 'failed',
  onRetry
}: {
  executionId: number
  reason?: 'failed' | 'cancelled'
  onRetry?: (executionId: number) => Promise<void>
}) {
  const [pending, setPending] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string>()
  const retryInFlight = useRef(false)
  const cancelled = reason === 'cancelled'
  return (
    <article className="flex flex-col gap-1" aria-label={cancelled ? 'Work cancelled' : 'Work stopped'}>
      <span className={activityRow}>
        <span className={cn(activityGlyph, cancelled ? activityQuiet : activityNotable)}>
          <AlertTriangle aria-hidden="true" />
        </span>
        <span className={cn('min-w-0 flex-1 truncate', cancelled && 'text-muted-foreground')}>
          {cancelled ? 'You cancelled this work' : 'Work stopped before it finished'}
        </span>
        {!cancelled && onRetry !== undefined && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className={activityAction}
            disabled={pending || submitted}
            onClick={() => {
              if (retryInFlight.current) return
              retryInFlight.current = true
              setPending(true)
              setError(undefined)
              void onRetry(executionId)
                .then(() => setSubmitted(true))
                .catch(() => {
                  retryInFlight.current = false
                  setError('That did not go through. Try again.')
                })
                .finally(() => setPending(false))
            }}
          >
            <RotateCw className="size-3" aria-hidden="true" />
            {submitted ? 'Retry requested' : pending ? 'Retrying' : 'Retry work'}
          </Button>
        )}
      </span>
      {error !== undefined && (
        <span className={cn(activityDetail, 'text-destructive')} role="alert">
          {error}
        </span>
      )}
    </article>
  )
}
